import * as AreenaApi from '../../service/areena-api/fetchers';
import { PaginationData } from '../../client/appTypings';
import { Thunk } from '../../client/configureStore';
import { isList } from '../../client/typeGuards';
import { pageSize } from './constants';
import { CardPageThunkPayload, CardPageThunkResponsePayload } from './types';
import { hbbTvMode } from '../../common/types';
import {
  ErrorName,
  ExpiredSessionError,
  LeanbackSerializedError,
  LoginApiError5xx,
  SilentError,
} from '../../client/error/errors';
import { createAsyncThunk } from '../createAsyncThunk';
import { logger } from '../../common/logger';
import { getUILanguage } from '../../client/localizations/language';

const getSafePagination = (pagination: PaginationData): PaginationData => ({
  ...pagination,
  limit: Math.round(pagination.limit),
  offset: Math.round(pagination.offset),
});

export const loadCardPage = createAsyncThunk<
  CardPageThunkResponsePayload,
  CardPageThunkPayload,
  {
    rejectValue: LeanbackSerializedError;
  }
>(
  'viewDataPaginated/loadPage',
  async (
    { viewId, listIndex, view, tabIndex, pageIndex, user },
    { getState, extra: { authFetch, appFeatures }, rejectWithValue }
  ) => {
    if (!view) {
      return rejectWithValue({
        name: ErrorName.ERROR,
        message: `View ${viewId} couldn't be found`,
      });
    }

    const pagination = getSafePagination({
      limit: pageSize,
      offset: pageIndex * pageSize,
    });

    const content = view?.tabs?.[tabIndex]?.content?.[listIndex];

    if (content?.type === 'list') {
      const list = view?.tabs?.[tabIndex]?.content?.[listIndex];
      const uri = isList(list) && list?.source.uri;

      if (!uri) {
        return rejectWithValue({
          name: ErrorName.ERROR,
          message: `Source URI couldn't be found for list ${listIndex} in tab ${tabIndex} in view ${viewId}`,
        });
      }

      const payloadHelpers = {
        languageCode: getUILanguage(),
        geoLocation: getState().currentAddress.geoLocation,
        isHbbtvMode: appFeatures.mode === hbbTvMode,
      };

      try {
        const cardListResponse = await AreenaApi.getList({
          authFetch,
          url: uri,
          paginationData: pagination,
          isAuthenticated: Boolean(user),
          silentlyConsumedStatusCodes: [500],
        });

        return {
          ...cardListResponse,
          ...payloadHelpers,
        };
      } catch (err) {
        if (err instanceof SilentError) {
          logger.debug(err);

          return payloadHelpers;
        }

        if (err instanceof Error) {
          return rejectWithValue({
            name: err.name,
            message: err.message,
            sessionCookie:
              err instanceof ExpiredSessionError ||
              err instanceof LoginApiError5xx
                ? err.sessionCookie
                : undefined,
            userId: user?.userId,
          });
        }

        return rejectWithValue({
          name: ErrorName.UNKNOWN_ERROR,
          message: `Unknown error during loading card page: ${String(err)}`,
        });
      }
    } else {
      return rejectWithValue({
        name: ErrorName.ERROR,
        message: `Unsupported content type ${content?.type}. Only 'highlightCard' and 'list' are supported.`,
      });
    }
  }
);

export const refreshCardList =
  ({
    viewId,
    listIndex,
    view,
    tabIndex,
    userLoginCookie,
    user,
  }: Omit<CardPageThunkPayload, 'pageIndex'>): Thunk<void> =>
  (dispatch, getState) => {
    const list =
      getState().viewData[viewId]?.tabs?.[tabIndex]?.lists[listIndex];

    if (!list) return;

    const pages = Math.floor(list.cards.length / pageSize);

    for (let pageIndex = 0; pageIndex < pages; pageIndex++) {
      void dispatch(
        loadCardPage({
          listIndex,
          pageIndex,
          viewId,
          view,
          tabIndex,
          userLoginCookie,
          user,
        })
      );
    }
  };
